"use client";

import { useCallback, useEffect, useRef } from "react";

/**
 * Returns a callback ref that reports the live border-box pixel height of the
 * attached element to `onHeight`, re-reporting on every resize — content
 * reflow, breakpoint/viewport change, font load, or a sibling popping in.
 *
 * Why this exists: the dashboard/account sticky-header stack positions each
 * layer with a `top` derived from the heights of the layers above it. Those
 * heights used to be hand-estimated constants (HR_ONLY_HEIGHT, TABS_ONLY_HEIGHT,
 * LEDGER_HEADER_HEIGHT, …) which drift from reality across the no-tabs `<hr>`
 * case, mobile vs desktop, and the badge/banner appearing — pinning a header
 * *into* the opaque band above it. Measuring the real band removes the guesswork.
 *
 * Notes:
 * - Rounds to the nearest integer so sub-pixel jitter doesn't churn React state.
 * - `getBoundingClientRect().height` is the border-box height, which for a
 *   sticky band is exactly its opaque extent (collapsed-out child margins are
 *   transparent space below the band, not clearance the next layer must avoid).
 * - Mirrors the ResizeObserver pattern already used by `s21-risk-banner.tsx`.
 */
export function useMeasuredHeight(onHeight: (px: number) => void) {
  // Keep the latest callback without re-creating the ref callback (which would
  // make React detach/reattach the observer on every render). Synced in an
  // effect — not during render — so the observer's `report` closure always
  // reads the current callback.
  const onHeightRef = useRef(onHeight);
  useEffect(() => {
    onHeightRef.current = onHeight;
  }, [onHeight]);

  const observerRef = useRef<ResizeObserver | null>(null);

  return useCallback((el: HTMLElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;

    if (!el) return;

    const report = () =>
      onHeightRef.current(Math.round(el.getBoundingClientRect().height));

    report();
    const observer = new ResizeObserver(report);
    observer.observe(el);
    observerRef.current = observer;
  }, []);
}
